import React, { useEffect, useRef, useState } from "react";
import { TbAlertTriangle } from "react-icons/tb";
import Modal from "../Modal/Modal";
import Spinner from "../Spinner/Spinner";
import Services from "../../services/Services";
import "./ConsentLockNotice.css";
import { temizMarkup } from "../SafeHtml/SafeHtml"; // FE-P2-2: sunucu HTML'i temizlenir

// Zorunlu onay (KVKK, Kullanıcı Sözleşmesi …) uyarı bandı + onay modalı.
// Müşteri ve iş ortağı (merchant) anasayfalarının ORTAK bileşeni: onay verilmeden panel
// kilitlidir, kilit yalnız buradan açılır. Onay verilince "consents:updated" yayınlanır
// (AuthContext kilidi ve sidebar tamamlanma oranı tazelenir).
export default function ConsentLockNotice({ consents = [] }) {
  const [consentModal, setConsentModal] = useState(null); // { consent }
  const [consentContent, setConsentContent] = useState("");
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentScrolled, setConsentScrolled] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  // Gövde ölçüsü ancak DOM'a bağlandıktan sonra bilinir: kısa metinlerde onScroll hiç
  // tetiklenmediği için "içerik sığıyorsa okunmuş say" dalı çalışmıyor ve "Kabul Ediyorum"
  // kalıcı kapalı kalıyordu. Ref callback + rAF ile ölçüm açılışta da yapılır.
  const consentBodyRef = useRef(null);
  const attachConsentBody = (node) => {
    consentBodyRef.current = node;
    if (!node) return;
    requestAnimationFrame(() => evaluateConsentScroll());
  };

  // Metnin %80'i okunmadan "Kabul Ediyorum" açılmaz.
  // ÖLÇÜM YALNIZ GERÇEK İÇERİK VARKEN yapılır: gövde, metin yüklenmeden (spinner'lı kısa
  // haliyle) de DOM'a bağlanıyor ve o anda "içerik sığıyor → okundu" dalı tetiklenip kapıyı
  // tamamen devre dışı bırakıyordu (uzun metin gelse de buton açık kalıyordu).
  const evaluateConsentScroll = () => {
    if (consentLoading || !consentContent) return;
    const el = consentBodyRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 4) { setConsentScrolled(true); return; }
    if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= 0.8) setConsentScrolled(true);
  };

  // İçerik yüklendikten sonra sıfırdan ölç: kısa metin anında "okundu" sayılır, uzun metin
  // kaydırma ister. Önce sıfırlanır ki içerik gelmeden üretilmiş yanlış bir "okundu" kalmasın.
  useEffect(() => {
    if (!consentContent) return;
    setConsentScrolled(false);
    const id = requestAnimationFrame(() => evaluateConsentScroll());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentContent]);

  const openConsentModal = async (consent) => {
    setConsentScrolled(false);
    setConsentContent("");
    setConsentModal({ consent });
    // Metni açan "bilgilendirildi" sayılır (profil Onaylarım ile aynı kural).
    if (consent?.type) {
      Services.MarkUserConsentInformed(localStorage.getItem("token"), consent.type).catch(() => {});
    }
    if (!consent?.webPageUri) return;
    setConsentLoading(true);
    try {
      const res = await Services.GetWebPage(localStorage.getItem("token"), consent.webPageUri, "tr");
      const result = res?.data?.result;
      setConsentContent(result?.contentTr || result?.contentTR || result?.content || "");
    } catch (err) {
      setConsentContent("");
    } finally {
      setConsentLoading(false);
    }
  };

  const acceptConsent = async () => {
    const consent = consentModal?.consent;
    if (!consent?.type || consentSaving) return;
    setConsentSaving(true);
    try {
      const res = await Services.SetUserConsent(localStorage.getItem("token"), consent.type, true);
      if (res?.data?.status) {
        setConsentModal(null);
        window.dispatchEvent(new CustomEvent("consents:updated"));
        window.dispatchEvent(new CustomEvent("profile:updated"));
      }
    } catch (err) {
      // durum değişmez; tekrar denenebilir
    } finally {
      setConsentSaving(false);
    }
  };

  if (!Array.isArray(consents) || consents.length === 0) return null;

  return (
    <>
      <section className="consent-lock" aria-live="polite">
        <div className="consent-lock-head">
          <TbAlertTriangle size={22} />
          <div>
            <strong>Onaylamanız gereken güncel metinler var</strong>
            <p>
              Hizmetlerimizi kullanmaya devam edebilmek için aşağıdaki metinleri inceleyip
              onaylamanız gerekmektedir. Onaylayana kadar diğer sayfalara erişim kapalıdır.
            </p>
          </div>
        </div>
        <div className="consent-lock-items">
          {consents.map((consent) => (
            <div className="consent-lock-item" key={consent.type}>
              <span className="consent-lock-title">{consent.title}</span>
              <button type="button" onClick={() => openConsentModal(consent)}>
                İncele ve Onayla
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Onay modalı: %80 okuma şartı + Kabul Ediyorum (SetUserConsent) */}
      <Modal
        isOpen={Boolean(consentModal)}
        title={consentModal?.consent?.title || "Sözleşme"}
        onClose={() => setConsentModal(null)}
        style={{ width: "760px", maxWidth: "92vw" }}
      >
        <div
          className="consent-lock-modal-body"
          ref={attachConsentBody}
          onScroll={evaluateConsentScroll}
        >
          {consentLoading && (
            <div className="consent-lock-modal-loading"><Spinner size={26} /></div>
          )}
          {!consentLoading && !consentContent && (
            <div className="consent-lock-modal-error">Metin yüklenemedi. Lütfen tekrar deneyin.</div>
          )}
          {!consentLoading && consentContent && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={temizMarkup(consentContent)} />
          )}
        </div>
        <div className="consent-lock-modal-actions">
          <button
            type="button"
            className="consent-lock-accept"
            disabled={consentLoading || !consentContent || !consentScrolled || consentSaving}
            title={!consentScrolled ? "Metni okumadan kabul edemezsiniz" : undefined}
            onClick={acceptConsent}
          >
            {consentSaving ? "Kaydediliyor..." : "Kabul Ediyorum"}
          </button>
        </div>
      </Modal>
    </>
  );
}
